// Copyright 2025, University of Maryland, All Rights Reserved
import { CASUser, TokenValidationResult } from "../types";

/**
 * Decode a JWT token without verification (client-side only)
 * Note: This does NOT verify the signature - verification must be done server-side
 */
export function decodeJWT(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.error("[CAS Auth] Invalid JWT format");
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch (error) {
    console.error("[CAS Auth] Failed to decode JWT:", error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const exp = decoded.exp as number;
  const now = Math.floor(Date.now() / 1000);

  return exp < now;
}

/**
 * Get the expiration time of a token in minutes
 */
export function getTokenExpirationMinutes(token: string): number | null {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return null;
  }

  const exp = decoded.exp as number;
  const now = Math.floor(Date.now() / 1000);
  const secondsRemaining = exp - now;

  return Math.floor(secondsRemaining / 60);
}

/**
 * Get the expiration time of a token in seconds
 */
export function getTokenExpirationSeconds(token: string): number | null {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return null;
  }

  const exp = decoded.exp as number;
  const now = Math.floor(Date.now() / 1000);

  return exp - now;
}

/**
 * Check if token needs refresh based on minutes before expiration
 */
export function shouldRefreshToken(
  token: string,
  minutesBeforeExpiry: number
): boolean {
  const minutesRemaining = getTokenExpirationMinutes(token);
  if (minutesRemaining === null) {
    return true; // Invalid token, should refresh
  }

  return minutesRemaining <= minutesBeforeExpiry;
}

/**
 * Extract user information from a JWT token
 */
export function extractUserFromToken(token: string): CASUser | null {
  const decoded = decodeJWT(token);
  if (!decoded) {
    return null;
  }

  return {
    token,
    username: (decoded.sub || decoded.username || decoded.uid) as string,
    email: decoded.email as string | undefined,
    name: decoded.name as string | undefined,
    umid: decoded.umid as string | undefined,
    exp: decoded.exp as number | undefined,
    iat: decoded.iat as number | undefined,
    attributes: decoded.attributes as Record<string, unknown> | undefined,
  };
}

/**
 * Validate a JWT token and extract user information
 */
export function validateToken(token: string | null): TokenValidationResult {
  if (!token) {
    return {
      valid: false,
      error: "No token provided",
    };
  }

  if (isTokenExpired(token)) {
    return {
      valid: false,
      expired: true,
      error: "Token expired",
    };
  }

  const user = extractUserFromToken(token);
  if (!user || !user.username) {
    return {
      valid: false,
      error: "Invalid token payload",
    };
  }

  return {
    valid: true,
    user,
  };
}